import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Package, Clock, Users } from 'lucide-react';
import { mockAnalytics } from '../../data/mockData';

const Analytics: React.FC = () => {
  const { kpis, ordersByStatus, revenueByCategory } = mockAnalytics;

  // Mock additional data for analytics
  const monthlyRevenue = [
    { month: 'Jan', revenue: 45000, orders: 1200 },
    { month: 'Feb', revenue: 52000, orders: 1350 },
    { month: 'Mar', revenue: 48000, orders: 1280 },
    { month: 'Apr', revenue: 61000, orders: 1450 },
    { month: 'May', revenue: 55000, orders: 1380 },
    { month: 'Jun', revenue: 67000, orders: 1520 },
  ];

  const topProducts = [
    { name: 'Panadol Extra', revenue: 8500, units: 450, margin: 38.8 },
    { name: 'Ventolin HFA', revenue: 7200, units: 380, margin: 36.9 },
    { name: 'Lipitor 20mg', revenue: 6800, units: 290, margin: 28.8 },
    { name: 'OxyContin 10mg', revenue: 5200, units: 95, margin: 29.3 },
    { name: 'Humira', revenue: 12500, units: 12, margin: 21.6 },
  ];

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    change: number;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, value, change, icon, color }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          <div className="flex items-center mt-2">
            {change >= 0 ? (
              <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
            )}
            <span className={`text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change >= 0 ? '+' : ''}{change}%
            </span>
            <span className="text-sm text-gray-500 ml-1">vs last month</span>
          </div>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Analytics & Reports</h1>
        <div className="flex space-x-3">
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
            Export Report
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200">
            Schedule Report
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Monthly Revenue"
          value={`$${kpis.totalRevenue.toLocaleString()}`}
          change={12.5}
          icon={<DollarSign className="h-6 w-6 text-white" />}
          color="bg-green-500"
        />
        <MetricCard
          title="Total Orders"
          value={kpis.totalOrders}
          change={8.2}
          icon={<Package className="h-6 w-6 text-white" />}
          color="bg-blue-500"
        />
        <MetricCard
          title="Avg Cycle Time"
          value={`${kpis.cycleTime}min`}
          change={-5.1}
          icon={<Clock className="h-6 w-6 text-white" />}
          color="bg-purple-500"
        />
        <MetricCard
          title="Active Customers"
          value="1,247"
          change={15.3}
          icon={<Users className="h-6 w-6 text-white" />}
          color="bg-orange-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Trend */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyRevenue}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value, name) => [
                name === 'revenue' ? `$${value.toLocaleString()}` : value,
                name === 'revenue' ? 'Revenue' : 'Orders'
              ]} />
              <Line type="monotone" dataKey="revenue" stroke="#3B82F6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Orders by Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Orders by Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ordersByStatus}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="status" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Category */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue by Category</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={revenueByCategory}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="revenue"
              >
                {revenueByCategory.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Products by Revenue</h3>
          <div className="space-y-4">
            {topProducts.map((product, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{product.name}</p>
                  <p className="text-sm text-gray-600">{product.units} units sold</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">${product.revenue.toLocaleString()}</p>
                  <p className="text-sm text-green-600">{product.margin.toFixed(1)}% margin</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Performance Indicators</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{kpis.fillRate}%</div>
            <div className="text-sm text-gray-600">Fill Rate</div>
            <div className="text-xs text-green-600 mt-1">Target: 95%</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{kpis.s8Compliance}%</div>
            <div className="text-sm text-gray-600">S8 Compliance</div>
            <div className="text-xs text-green-600 mt-1">Excellent</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600">{kpis.coldChainExceptions}</div>
            <div className="text-sm text-gray-600">Cold Chain Exceptions</div>
            <div className="text-xs text-yellow-600 mt-1">This month</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">{kpis.expiryRisk}</div>
            <div className="text-sm text-gray-600">Items at Expiry Risk</div>
            <div className="text-xs text-orange-600 mt-1">Next 90 days</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
